/**
 * Vídeo na bolha do chat — miniatura + botão, sem baixar nada até o clique.
 *
 * **Por que existe (06/08/2026).** O Augusto observou que ao abrir uma conversa
 * o vídeo parecia já estar carregando, e que isso pesaria no servidor com vários
 * usuários simultâneos. Medido no navegador, ele estava certo:
 *
 *     preload configurado:     "metadata"
 *     readyState dos players:  4   ← HAVE_ENOUGH_DATA
 *
 * `readyState 4` significa "já baixei o suficiente para tocar até o fim". O
 * `preload="metadata"` é uma SUGESTÃO — o Chrome a descarta com frequência,
 * sobretudo em arquivos pequenos. Eu havia escrito num comentário que ele
 * "baixa só o cabeçalho": era afirmação minha, não fato medido.
 *
 * **A única garantia é não existir o elemento.** Enquanto houver um `<video>`
 * na página, o navegador decide sozinho o que buscar. Por isso aqui o `<video>`
 * só é montado depois do clique — antes disso há uma miniatura (uma imagem
 * comum) e um botão.
 *
 * A miniatura é capturada no CLIENTE, durante a gravação: o navegador já tem a
 * imagem na tela, então custa um `drawImage`. Gerá-la no servidor exigiria
 * ffmpeg no container `web`, que deliberadamente não o tem — são ~100MB que
 * evitamos ao instalá-lo só nos workers.
 *
 * Vídeos ANTIGOS não têm miniatura: mostram um cartão com duração e o mesmo
 * botão. O ganho de rede é idêntico; muda só o quanto se reconhece antes de
 * abrir.
 *
 * **Variante `cartao` (18/09/2026).** O feed de postagens (Início do portal)
 * passou a usar este mesmo componente, mas um post não é uma bolha — ocupa a
 * largura do cartão, na proporção do vídeo (`largura`/`altura`; sem elas,
 * 16:9). A prop `variante` escolhe entre `"balao"` (padrão, o de sempre, 280px)
 * e `"cartao"` (`w-full`).
 *
 * **Por que o endereço fica congelado após o clique.** O feed recarrega (a
 * cada 30s, ou ao voltar à aba) e o backend RE-ASSINA a URL a cada leitura —
 * `?expires=&sig=` mudam mesmo apontando para o mesmo arquivo. Se o player
 * seguisse a prop `src` a cada render, o vídeo que está tocando recomeçaria
 * do zero a cada atualização do feed. Por isso, ao clicar, o componente GUARDA
 * o endereço em uso e só troca se o ARQUIVO mudar de fato (comparando a URL
 * sem a query string) — nunca por causa de uma simples re-assinatura.
 */
import { useState } from "react";
import type { CSSProperties } from "react";
import { Play, Video as VideoIcon } from "lucide-react";

import { cn } from "@/lib/utils";

export interface VideoAttachmentProps {
  /** URL assinada do vídeo (re-assinada no render pelo backend). */
  src: string;
  /** URL assinada da miniatura, quando existe. */
  posterSrc?: string;
  filename?: string;
  durationSec?: number | null;
  /**
   * `balao` (padrão): 280 px fixos — a bolha do chat não tem largura própria
   * (ver o comentário no botão). `cartao`: largura do post, na proporção do vídeo.
   */
  variante?: "balao" | "cartao";
  /** Dimensões do vídeo, para a proporção do `cartao`. */
  largura?: number | null;
  altura?: number | null;
  className?: string;
}

function formatarDuracao(seg: number): string {
  const m = Math.floor(seg / 60);
  const s = Math.floor(seg % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** O que identifica o ARQUIVO: o caminho, sem `?expires=&sig=`, que o backend
 *  troca a cada leitura. */
function identidade(url: string): string {
  return url.split("?", 1)[0];
}

function proporcao(largura?: number | null, altura?: number | null): string {
  return largura && altura && largura > 0 && altura > 0 ? `${largura} / ${altura}` : "16 / 9";
}

export function VideoAttachment({
  src,
  posterSrc,
  filename,
  durationSec,
  variante = "balao",
  largura,
  altura,
  className,
}: VideoAttachmentProps) {
  // Congelado no clique: o feed recarrega e a URL re-assinada muda; o vídeo que
  // está tocando não pode recomeçar por isso. Mas se o componente passar a
  // mostrar OUTRO arquivo (lista que reaproveita a posição), volta à miniatura.
  const [emReproducao, setEmReproducao] = useState<{ src: string; poster?: string } | null>(null);

  // O congelamento precisa ser DESCARTADO quando o arquivo muda — não só
  // mascarado pelo `tocando` abaixo. Sem isto: A toca, o slot passa a mostrar
  // B (aí `tocando` já dava false, e parecia correto), mas `emReproducao`
  // continuava guardando A; se a lista devolvesse A de novo — mesmo
  // re-assinado — `tocando` recalculava true sozinho e o player religava sem
  // clique nenhum, com uma URL que já podia ter expirado.
  //
  // Ajuste de estado durante a renderização (padrão do React para "resetar
  // estado quando uma prop muda", preferível a um `useEffect` aqui: um efeito
  // rodaria DEPOIS de pintar o `<video>` velho na tela por um frame).
  if (emReproducao !== null && identidade(emReproducao.src) !== identidade(src)) {
    setEmReproducao(null);
  }

  const tocando = emReproducao !== null && identidade(emReproducao.src) === identidade(src);

  const ehCartao = variante === "cartao";
  const razao = proporcao(largura, altura);
  // I-3 (revisão final, 19/09/2026): `aspect-ratio` sozinho só deriva UMA
  // dimensão. Aqui a largura já vem fixada por `w-full` e a altura por
  // `max-h-[70vh]` — duas fontes independentes —, então um vídeo retrato
  // (ex.: 720×1280) tinha a altura cortada em 70vh sem a largura acompanhar,
  // e virava uma faixa larga e baixa (medido no E2E: 698×345px numa caixa
  // que deveria ficar alta). `maxWidth` limita a largura pela MESMA altura
  // disponível: a maior largura que ainda cabe em 70vh de altura é
  // `70vh * largura/altura` — a mesma conta do `aspectRatio`, por isso reusa
  // `razao` (inclusive o fallback 16/9 sem dimensões).
  const caixa: CSSProperties | undefined = ehCartao
    ? { aspectRatio: razao, maxWidth: `calc(70vh * ${razao})` }
    : undefined;

  // Depois do clique: o <video> nasce com `autoPlay`, porque o clique JÁ é o
  // gesto do usuário — sem isso ele precisaria clicar duas vezes.
  if (tocando && emReproducao) {
    return (
      <div
        className={cn("space-y-1", className)}
        data-testid="attachment-video"
        data-variante={variante}
        data-proporcao={ehCartao ? razao : undefined}
      >
        {ehCartao ? (
          <div
            className="mx-auto max-h-[70vh] w-full overflow-hidden rounded-lg bg-black"
            style={caixa}
          >
            <video
              src={emReproducao.src}
              poster={emReproducao.poster}
              controls
              autoPlay
              playsInline
              className="h-full w-full bg-black object-contain"
              data-testid="attachment-video-player"
            />
          </div>
        ) : (
          <video
            src={emReproducao.src}
            poster={emReproducao.poster}
            controls
            autoPlay
            playsInline
            className="w-[280px] max-w-full rounded-md bg-black"
            data-testid="attachment-video-player"
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={cn("space-y-1", className)}
      data-testid="attachment-video"
      data-variante={variante}
      data-proporcao={ehCartao ? razao : undefined}
    >
      <button
        type="button"
        onClick={() => setEmReproducao({ src, poster: posterSrc })}
        // LARGURA EXPLÍCITA, não `w-full`. A bolha do chat não tem largura
        // própria — ela se ajusta ao conteúdo. Com `w-full` surge uma
        // circularidade: a bolha pergunta ao conteúdo quanto ele quer, o
        // conteúdo responde "100% da bolha", e o navegador resolve colapsando.
        // Medido em produção: o cartão saiu com 40x40px dentro de uma bolha de
        // 66px — o "quadradinho" que o Augusto viu.
        //
        // `max-w-full` mantém o respeito ao limite da bolha em telas estreitas.
        // Na variante `cartao` o post FORNECE a largura (o card), então aí sim
        // `w-full` é o comportamento certo — sem a circularidade acima.
        className={
          ehCartao
            ? "relative mx-auto block max-h-[70vh] w-full overflow-hidden rounded-lg bg-black"
            : "relative block w-[280px] max-w-full overflow-hidden rounded-md bg-black/90"
        }
        style={caixa}
        aria-label={`Reproduzir vídeo${filename ? ` ${filename}` : ""}`}
        data-testid="attachment-video-abrir"
      >
        {posterSrc ? (
          <img
            src={posterSrc}
            alt={filename || "miniatura do vídeo"}
            loading="lazy"
            className={ehCartao ? "block h-full w-full object-contain" : "block w-full"}
            data-testid="attachment-video-poster"
          />
        ) : (
          // Sem miniatura (vídeos anteriores a 06/08/2026): cartão neutro. Não
          // baixa nada — que é o ponto — e ainda diz que ali há um vídeo.
          <div
            className={cn(
              "flex w-full items-center justify-center bg-neutral-800",
              ehCartao ? "h-full" : "aspect-video"
            )}
            data-testid="attachment-video-sem-poster"
          >
            <VideoIcon className="h-10 w-10 text-white/40" />
          </div>
        )}

        {/* Botão de play sobreposto — o affordance que o Augusto pediu. */}
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-black/55 ring-1 ring-white/30">
            <Play className="ml-0.5 h-7 w-7 fill-white text-white" />
          </span>
        </span>

        {typeof durationSec === "number" && durationSec > 0 && (
          <span className="absolute bottom-1.5 right-1.5 rounded bg-black/70 px-1.5 py-0.5 text-[11px] font-medium text-white tabular-nums">
            {formatarDuracao(durationSec)}
          </span>
        )}
      </button>
    </div>
  );
}
